import { useEffect, useState } from 'react';
import { UserCog, Clock, Eye, ChevronLeft, Building2, User } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { adminApi } from '../../../lib/api';
import { toast } from 'sonner';

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

export default function ImpersonationAuditPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);

  const load = async () => {
    setIsLoading(true);
    try {
      const params = { limit: 100 };
      if (filter !== 'all') params.target_type = filter;
      const res = await adminApi.getImpersonationAudit(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error("Impossible de charger l'audit");
    } finally { setIsLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const openDetail = async (session) => {
    setSelected(session);
    setDetails(null);
    try {
      const res = await adminApi.getImpersonationSession(session.id);
      setDetails(res.data);
    } catch (e) {
      toast.error('Impossible de charger le détail');
    }
  };

  return (
    <div className="space-y-6" data-testid="impersonation-audit-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl">Audit — Voir en tant que</h1>
          <p className="text-muted-foreground mt-1">
            Journal complet des sessions d'impersonation (conformité RGPD).
          </p>
        </div>
        <Badge variant="outline" className="text-sm">Total : {total}</Badge>
      </div>

      <div className="flex gap-2">
        {[
          { v: 'all', l: 'Tous' },
          { v: 'company', l: 'Entreprises' },
          { v: 'client', l: 'Clients' },
        ].map(f => (
          <Button
            key={f.v}
            variant={filter === f.v ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.v)}
            data-testid={`audit-filter-${f.v}`}
          >{f.l}</Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserCog className="w-4 h-4 text-amber-600" />
            Sessions récentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center text-muted-foreground py-6">Chargement…</p>
          ) : items.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">Aucune session enregistrée.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-2">Cible</th>
                    <th className="py-2 pr-2">Type</th>
                    <th className="py-2 pr-2">Admin</th>
                    <th className="py-2 pr-2">Début</th>
                    <th className="py-2 pr-2">Durée</th>
                    <th className="py-2 pr-2">Actions</th>
                    <th className="py-2 pr-2 text-right">Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(s => (
                    <tr key={s.id} className="border-b hover:bg-muted/30" data-testid={`audit-row-${s.id}`}>
                      <td className="py-2 pr-2 font-medium">{s.target_name || s.target_id}</td>
                      <td className="py-2 pr-2">
                        <Badge variant="outline" className="gap-1">
                          {s.target_type === 'company'
                            ? <Building2 className="w-3 h-3" />
                            : <User className="w-3 h-3" />}
                          {s.target_type}
                        </Badge>
                      </td>
                      <td className="py-2 pr-2 text-muted-foreground">{s.admin_email || s.admin_user_id?.slice(0, 8)}</td>
                      <td className="py-2 pr-2 text-muted-foreground">{formatDate(s.started_at)}</td>
                      <td className="py-2 pr-2">
                        <div className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          <span className={s.ended_at ? '' : 'text-amber-600 font-medium'}>
                            {s.ended_at ? formatDuration(s.duration_seconds) : 'En cours'}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 pr-2">{s.action_count || 0}</td>
                      <td className="py-2 pr-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openDetail(s)} data-testid={`audit-view-${s.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && (setSelected(null), setDetails(null))}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-4 h-4 text-amber-600" />
              Session — {selected?.target_name}
            </DialogTitle>
            <DialogDescription>
              Admin : {selected?.admin_email} • Début : {formatDate(selected?.started_at)} • Durée : {formatDuration(selected?.duration_seconds)}
            </DialogDescription>
          </DialogHeader>

          {!details ? (
            <p className="text-center text-muted-foreground py-6">Chargement du détail…</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Session ID :</span> <code className="text-xs">{details.id}</code></div>
                <div><span className="text-muted-foreground">Cible :</span> {details.target_name} ({details.target_email || '—'})</div>
                <div><span className="text-muted-foreground">Nb. actions :</span> {details.action_count || 0}</div>
                <div><span className="text-muted-foreground">Fin :</span> {details.ended_at ? formatDate(details.ended_at) : <span className="text-amber-600">En cours</span>}</div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Actions clés (dernières entrées)</p>
                <div className="max-h-96 overflow-y-auto border rounded-md">
                  {(details.actions || []).length === 0 ? (
                    <p className="text-center text-muted-foreground py-4 text-sm">Aucune action d'écriture enregistrée.</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left py-2 px-2">Date</th>
                          <th className="text-left py-2 px-2">Méthode</th>
                          <th className="text-left py-2 px-2">Chemin</th>
                          <th className="text-left py-2 px-2">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(details.actions || []).slice().reverse().map((a, i) => (
                          <tr key={i} className="border-t">
                            <td className="py-1.5 px-2 whitespace-nowrap text-muted-foreground">{formatDate(a.at)}</td>
                            <td className="py-1.5 px-2 font-mono">
                              <Badge variant="outline" className="text-[10px]">{a.method}</Badge>
                            </td>
                            <td className="py-1.5 px-2 font-mono truncate max-w-md">{a.path}</td>
                            <td className="py-1.5 px-2">
                              <span className={`font-medium ${a.status >= 400 ? 'text-red-600' : 'text-emerald-600'}`}>{a.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
